import { html } from 'lit-html';
import { api, get } from '../api.js';
import { act, app, confirmDialog, helpLink, isAdmin, toast, type Page } from '../app.js';
import { when } from '../format.js';
import { health, loadHealth } from './dashboard.js';

const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
type Day = (typeof DAYS)[number];
export interface Hours {
  tz: string;
  windows: { days: Day[]; start: string; end: string }[];
}

interface Global {
  version: number;
  workerMode: 'capture' | 'detect' | 'enforce';
  dryRun: boolean;
  enforceAllQueues: boolean;
  defaultQueueMode: 'off' | 'dry_run' | 'enforce';
  wrapupTimeoutCode: string;
  maxEnforcementDelaySeconds: number;
  enforcementHours: Hours | null;
  graceThreshold: number;
  graceWindowDays: number;
  graceScope: 'agent_queue' | 'agent';
  ambiguousRecovery: 'retry' | 'conservative';
  reactivationSkewSeconds: number;
  reconcileIntervalMinutes: number;
  rawEventRetentionDays: number;
  confirmViaConversationApi: boolean;
  reportTimezone: string;
  alarmEmails: string[];
  updatedBy: string;
  updatedAt: string;
}

const TIMEZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'UTC'];

let saved: Global | null = null;
let draft: Global | null = null;

type RecipientStatus = 'confirmed' | 'pending' | 'not_subscribed' | 'unmanaged';
let recipientStatus = new Map<string, RecipientStatus>();
let recipientsError: string | null = null;

const STATUS_PILL: Record<RecipientStatus | 'unsaved', [string, string]> = {
  confirmed: ['positive', 'Receiving alarms'],
  pending: ['warning', 'Waiting for confirmation'],
  not_subscribed: ['neutral', 'Subscribing…'],
  unmanaged: ['neutral', 'AWS only'],
  unsaved: ['neutral', 'Not saved yet'],
};

/** Alarm email recipients: edited in the draft like every other rule, saved with the save bar. */
function alarmEmailsPanel(d: Global, admin: boolean) {
  const add = (e: Event) => {
    e.preventDefault();
    const input = (e.target as HTMLFormElement).elements.namedItem('email') as HTMLInputElement;
    const email = input.value.trim().toLowerCase();
    if (!email || !input.checkValidity()) return;
    if (!d.alarmEmails.includes(email)) set('alarmEmails', [...d.alarmEmails, email].sort());
    input.value = '';
  };
  const status = (email: string): [string, string] =>
    STATUS_PILL[saved?.alarmEmails.includes(email) ? (recipientStatus.get(email) ?? 'not_subscribed') : 'unsaved'];
  return html`
    <section class="panel">
      <h2>Alarm emails</h2>
      <p class="hint">Who AWS emails when an alarm goes off: failed deactivations, Genesys permission errors, no Genesys events during
        business hours, a stuck or failing event queue. Each new address gets an email from <b>AWS Notifications</b> and must click
        <b>Confirm subscription</b> before alarms reach it.</p>
      ${recipientsError ? html`<div class="banner warning">${recipientsError}</div>` : ''}
      ${d.alarmEmails.length
        ? html`<ul class="recipients">${d.alarmEmails.map((email) => {
            const [tone, label] = status(email);
            return html`<li><span class="email">${email}</span><span class="pill ${tone}">${label}</span>
              ${admin ? html`<button class="btn destructive outline compact" @click=${() => set('alarmEmails', d.alarmEmails.filter((x) => x !== email))}>Remove</button>` : ''}</li>`;
          })}</ul>`
        : html`<p class="muted">Nobody gets alarm emails.</p>`}
      ${admin
        ? html`<form class="inline compact" @submit=${add}>
            <input name="email" type="email" placeholder="name@company.com" aria-label="Alarm email address" required maxlength="320">
            <button class="btn compact">Add</button>
          </form>
          <p class="hint">Added or removed addresses take effect when you click Save.</p>`
        : ''}
    </section>`;
}

export function hoursSummary(h: unknown): string {
  const hours = h as Hours | null;
  if (!hours?.windows?.length) return 'Always';
  return hours.windows
    .map((w) => `${w.days.map((d) => d[0]!.toUpperCase() + d.slice(1)).join(', ')} ${w.start}–${w.end}`)
    .join('; ') + ` (${hours.tz.replace('America/', '').replace('_', ' ')})`;
}

function changes(): Partial<Global> {
  if (!saved || !draft) return {};
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(draft) as (keyof Global)[]) {
    if (['version', 'updatedBy', 'updatedAt'].includes(k)) continue;
    if (JSON.stringify(draft[k]) !== JSON.stringify(saved[k])) out[k] = draft[k];
  }
  return out as Partial<Global>;
}

function set<K extends keyof Global>(k: K, v: Global[K]) {
  draft = { ...draft!, [k]: v };
  app.rerender();
}

async function save() {
  const diff = changes();
  if (Object.keys(diff).length === 0) return toast('Nothing to save', 'info');
  const risky =
    (diff.dryRun === false) || (diff.workerMode === 'enforce' && !draft!.dryRun) || (diff.enforceAllQueues === true && draft!.defaultQueueMode === 'enforce');
  if (risky) {
    const c = await confirmDialog({
      title: 'Turn on live enforcement?',
      body: html`Agents who miss ACW on queues set to <b>Enforce</b> will be deactivated on that queue${
        health && !health.settings.allowLiveEnforcement ? html` (still blocked by the infrastructure switch until the deployment allows it)` : ''
      }. You can undo this at any time with <b>Emergency stop</b>.`,
      confirmLabel: 'Save and enforce',
    });
    if (!c.ok) return app.rerender();
  }
  await act(() => api('PATCH', '/settings', diff), 'Settings saved', rules.load);
}

const num = (k: keyof Global, min: number, max: number, suffix: string) => html`
  <input type="number" min=${min} max=${max} .value=${String(draft![k])} ?disabled=${!isAdmin()}
    @change=${(e: Event) => set(k, Number((e.target as HTMLInputElement).value) as never)}> <span class="muted">${suffix}</span>`;

export interface HoursEditorLabels {
  /** Shown when no hours are set (null). */
  none: unknown;
  setLabel: string;
  clearLabel: string;
}

/** Reusable enforcement-hours editor (global Rules page and per-queue overrides). */
export function hoursEditor(
  h: Hours | null,
  update: (next: Hours | null) => void,
  admin: boolean,
  labels: HoursEditorLabels = { none: html`<p>Enforce at <b>any time</b>.</p>`, setLabel: 'Limit to set hours', clearLabel: 'Enforce at any time' },
) {
  if (!h) {
    return html`${labels.none}
      ${admin ? html`<button class="btn quiet" @click=${() => update({ tz: 'America/New_York', windows: [{ days: ['mon', 'tue', 'wed', 'thu', 'fri'], start: '08:00', end: '20:00' }] })}>${labels.setLabel}</button>` : ''}`;
  }
  const win = (i: number, patch: Partial<Hours['windows'][number]>) =>
    update({ ...h, windows: h.windows.map((w, j) => (j === i ? { ...w, ...patch } : w)) });
  return html`
    <label class="field compact">Time zone <select ?disabled=${!admin} @change=${(e: Event) => update({ ...h, tz: (e.target as HTMLSelectElement).value })}>
      ${[...new Set([h.tz, ...TIMEZONES])].map((tz) => html`<option ?selected=${tz === h.tz}>${tz}</option>`)}
    </select></label>
    ${h.windows.map((w, i) => html`
      <div class="window compact">
        <div class="days">${DAYS.map((d) => html`
          <label class="day ${w.days.includes(d) ? 'on' : ''}"><input type="checkbox" ?checked=${w.days.includes(d)} ?disabled=${!admin}
            @change=${(e: Event) => win(i, { days: (e.target as HTMLInputElement).checked ? DAYS.filter((x) => x === d || w.days.includes(x)) : w.days.filter((x) => x !== d) })}>${d}</label>`)}</div>
        <input type="time" .value=${w.start} ?disabled=${!admin} @change=${(e: Event) => win(i, { start: (e.target as HTMLInputElement).value })}>
        to <input type="time" .value=${w.end} ?disabled=${!admin} @change=${(e: Event) => win(i, { end: (e.target as HTMLInputElement).value })}>
        ${admin && h.windows.length > 1 ? html`<button class="btn destructive outline compact" @click=${() => update({ ...h, windows: h.windows.filter((_, j) => j !== i) })}>Remove</button>` : ''}
      </div>`)}
    <p class="hint">A window whose end is earlier than its start runs past midnight. The timeout's own time is what's compared.</p>
    ${admin ? html`<button class="btn quiet" @click=${() => update({ ...h, windows: [...h.windows, { days: ['sat'], start: '09:00', end: '17:00' }] })}>Add window</button>
      <button class="btn quiet" @click=${() => update(null)}>${labels.clearLabel}</button>` : ''}`;
}

export const rules: Page = {
  title: 'Rules',
  async load() {
    const [s, r] = await Promise.all([
      get<{ global: Global }>('/settings'),
      get<{ recipients: { email: string; status: RecipientStatus }[] }>('/alarm-recipients').catch((err: Error) => err),
      loadHealth(),
    ]);
    saved = s.global;
    draft = structuredClone(s.global);
    recipientsError = r instanceof Error ? `Couldn't check alarm email subscriptions: ${r.message}` : null;
    recipientStatus = new Map(r instanceof Error ? [] : r.recipients.map((x) => [x.email, x.status]));
  },
  view() {
    if (!draft) return html`<p>Loading…</p>`;
    const admin = isAdmin();
    const d = draft;
    const dirty = Object.keys(changes()).length > 0;
    const radio = <K extends keyof Global>(k: K, options: [Global[K], string, string][]) => html`
      <div class="radios">${options.map(([v, label, help]) => html`
        <label class="radio ${d[k] === v ? 'on' : ''}"><input type="radio" name=${k} ?checked=${d[k] === v} ?disabled=${!admin} @change=${() => set(k, v)}>
          <b>${label}</b><span class="muted">${help}</span></label>`)}</div>`;
    return html`
      <h1>Rules ${helpLink('rules')}</h1>
      <p class="hint">Global settings. Per-queue overrides live on the Queues page. Changes apply within seconds and are recorded in the audit log.
        Last changed ${when(saved?.updatedAt)} by ${saved?.updatedBy}.</p>

      <section class="panel">
        <h2>Enforcement</h2>
        ${radio('workerMode', [
          ['capture', 'Capture', 'Store raw events only (setup phase)'],
          ['detect', 'Detect', 'Record timeouts and wrap-up times; never change anything in Genesys'],
          ['enforce', 'Enforce', 'Apply the rules below to listed queues'],
        ])}
        <label class="switch"><input type="checkbox" class="toggle" .checked=${d.dryRun} ?disabled=${!admin} @change=${(e: Event) => set('dryRun', (e.target as HTMLInputElement).checked)}>
          <span><b>Dry run everywhere</b> — record "would deactivate" and never deactivate anyone. Overrides every queue.</span></label>
        ${health && !health.settings.allowLiveEnforcement
          ? html`<div class="banner warning">The infrastructure switch <code>allowLiveEnforcement</code> is off, so nothing is deactivated regardless of these settings.</div>` : ''}
        <label class="switch compact"><input type="checkbox" class="toggle" .checked=${d.enforceAllQueues} ?disabled=${!admin} @change=${(e: Event) => set('enforceAllQueues', (e.target as HTMLInputElement).checked)}>
          <span><b>Apply to every queue</b>, not just listed ones, using mode
          <select ?disabled=${!admin || !d.enforceAllQueues} @change=${(e: Event) => set('defaultQueueMode', (e.target as HTMLSelectElement).value as Global['defaultQueueMode'])}>
            ${['off', 'dry_run', 'enforce'].map((m) => html`<option value=${m} ?selected=${d.defaultQueueMode === m}>${m.replace('_', ' ')}</option>`)}
          </select></span></label>
      </section>

      <section class="panel">
        <h2>Grace count</h2>
        <p class="compact">Deactivate on timeout number ${num('graceThreshold', 1, 100, '')} within ${num('graceWindowDays', 1, 365, 'days')}, counted
          <select ?disabled=${!admin} @change=${(e: Event) => set('graceScope', (e.target as HTMLSelectElement).value as Global['graceScope'])}>
            <option value="agent_queue" ?selected=${d.graceScope === 'agent_queue'}>per agent and queue</option>
            <option value="agent" ?selected=${d.graceScope === 'agent'}>per agent, across all queues</option>
          </select>.</p>
        <p class="hint">1 means every timeout. Earlier timeouts in the window are recorded as grace warnings.</p>
      </section>

      <section class="panel">
        <h2>Enforcement hours</h2>
        ${hoursEditor(d.enforcementHours, (next) => set('enforcementHours', next), admin)}
      </section>

      ${alarmEmailsPanel(d, admin)}

      <details class="panel">
        <summary><h2>Advanced</h2></summary>
        <div class="form-grid">
          <label>Don't enforce timeouts older than</label><div>${num('maxEnforcementDelaySeconds', 30, 86400, 'seconds (e.g. after an outage)')}</div>
          <label>Double-check the code on the conversation</label><div><input type="checkbox" class="toggle" .checked=${d.confirmViaConversationApi} ?disabled=${!admin} @change=${(e: Event) => set('confirmViaConversationApi', (e.target as HTMLInputElement).checked)}></div>
          <label>If a deactivation's outcome is unknown</label><div><select ?disabled=${!admin} @change=${(e: Event) => set('ambiguousRecovery', (e.target as HTMLSelectElement).value as Global['ambiguousRecovery'])}>
            <option value="retry" ?selected=${d.ambiguousRecovery === 'retry'}>Retry once the event stream proves it didn't land</option>
            <option value="conservative" ?selected=${d.ambiguousRecovery === 'conservative'}>Mark failed for manual review</option></select></div>
          <label>Clock tolerance</label><div>${num('reactivationSkewSeconds', 0, 300, 'seconds')}</div>
          <label>Backstop check of open records every</label><div>${num('reconcileIntervalMinutes', 1, 1440, 'minutes')}</div>
          <label>Keep raw events for</label><div>${num('rawEventRetentionDays', 1, 3650, 'days')}</div>
          <label>Report time zone</label><div><select ?disabled=${!admin} @change=${(e: Event) => set('reportTimezone', (e.target as HTMLSelectElement).value)}>
            ${[...new Set([d.reportTimezone, ...TIMEZONES])].map((tz) => html`<option ?selected=${tz === d.reportTimezone}>${tz}</option>`)}</select></div>
          <label>Trigger wrap-up code</label><div><code>${d.wrapupTimeoutCode}</code></div>
        </div>
      </details>

      ${admin ? html`<div class="savebar ${dirty ? 'dirty' : ''}">
        <span>${dirty ? `${Object.keys(changes()).length} unsaved change(s)` : 'All changes saved'}</span>
        <button class="btn quiet" ?disabled=${!dirty} @click=${() => { draft = structuredClone(saved); app.rerender(); }}>Discard</button>
        <button class="btn primary" ?disabled=${!dirty} @click=${save}>Save</button>
      </div>` : html`<p class="muted">You have view-only access.</p>`}`;
  },
};
